import React, { useEffect } from 'react'
import { hcRed, hcBlue, hcBlack } from 'components/common/Constants'
import { initializeHighcharts, titleWithTooltip } from 'components/common/Utils'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import I18n from 'i18n'
import { DailyLogRow } from 'components/common/types/questionnaires/DailyLogRow'
import GenericPlaceholder from 'components/common/GenericPlaceholder'
import { calculateWeeklyAverages } from 'components/analysis/dashboards/questionnaire/Utils'
import _ from 'lodash'

interface Props {
  dailyLogRows?: DailyLogRow[]
}

const StressAndMoodDailyChart: React.FC<Props> = ({ dailyLogRows }) => {
  const minimumDataPointsForAverage = 21

  useEffect(() => {
    initializeHighcharts(true)
  }, [])

  if (_.isEmpty(dailyLogRows) || !Array.isArray(dailyLogRows)) {
    return (
      <GenericPlaceholder title={I18n.t('components.dashboards.questionnaire.stress_and_mood.title')} />
    )
  }

  let moodData = dailyLogRows?.map(entry => ({
    x: entry.date_only,
    y: entry.wellness_mood,
    formatted_date: entry.formatted_date
  }))

  let stressData = dailyLogRows?.map(entry => ({
    x: entry.date_only,
    y: entry.wellness_stress,
    formatted_date: entry.formatted_date
  }))

  const xAxisOptions: Highcharts.XAxisOptions = {
    title: {
      text: ''
    },
    type: 'datetime',
    dateTimeLabelFormats: {
      day: '%d %b'
    }
  }

  if (dailyLogRows?.length > minimumDataPointsForAverage) {
    moodData = calculateWeeklyAverages(moodData)
    stressData = calculateWeeklyAverages(stressData)
  }

  // The entire chart will appear white/empty when only one datapoint for the line is present.
  // In this case, enable datapoints
  const showMarkers = moodData.length === 1 || stressData.length === 1

  return (
    <>
      <HighchartsReact
        highcharts={Highcharts}
        options={{
          title: titleWithTooltip(
            I18n.t('components.dashboards.questionnaire.stress_and_mood.title'),
            I18n.t('components.dashboards.questionnaire.stress_and_mood.info')
          ),
          series: [
            {
              type: 'line',
              data: moodData,
              name: I18n.t('components.dashboards.questionnaire.stress_and_mood.mood_line.name'),
              color: hcBlue,
              lineWidth: 2,
              marker: {
                enabled: showMarkers,
                states: {
                  hover: {
                    enabled: true
                  }
                }
              },
              tooltip: {
                headerFormat: '',
                pointFormat: I18n.t('components.dashboards.questionnaire.stress_and_mood.mood_line.tooltip')
              }
            },
            {
              type: 'line',
              data: stressData,
              name: I18n.t('components.dashboards.questionnaire.stress_and_mood.stress_line.name'),
              color: hcRed,
              lineWidth: 2,
              marker: {
                enabled: showMarkers,
                states: {
                  hover: {
                    enabled: true
                  }
                }
              },
              tooltip: {
                headerFormat: '',
                pointFormat: I18n.t('components.dashboards.questionnaire.stress_and_mood.stress_line.tooltip')
              }
            }
          ],
          plotOptions: {
            series: {
            },
            scatter: {
              dataLabels: {
                enabled: false
              },
              stacking: 'normal',
              enableMouseTracking: true
            },
            line: {
              color: hcBlack,
              marker: {
                enabled: true
              }
            }
          },
          yAxis: {
            title: {
              text: I18n.t('components.dashboards.questionnaire.stress_and_mood.yaxis')
            },
            min: 0
          },
          xAxis: xAxisOptions,
          legend: {
            enabled: true,
            layout: 'horizontal',
            align: 'center',
            verticalAlign: 'top'
          }
        }}
      />
    </>
  )
}

export default StressAndMoodDailyChart
